import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectNotifications } from '~/slices/appSlice';
import { useToast } from '~/customHooks/useToast';
import BiaIcon from '~/ui/3dbiaUI/BiaIcon/BiaIcon';
import BiaSpinner from '~/ui/3dbiaUI/BiaSpinner/BiaSpinner';
import { useTheme } from '~/utils/theme';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';

interface ToastItemProps {
  id: string;
  text: string;
  type: 'success' | 'error' | 'info' | 'working';
  onClose: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ id, text, type, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    // Trigger show animation after component mounts
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const accent =
    type === 'success' ? theme.success
    : type === 'error' ? theme.danger
    : type === 'working' ? theme.warning
    : theme.info;

  const getIcon = (): IconProp | null => {
    switch (type) {
      case 'success':
        return 'check-circle';
      case 'error':
        return 'times-circle';
      case 'working':
        return null; // Will use spinner instead
      case 'info':
      default:
        return 'info-circle';
    }
  };

  const handleClose = () => {
    setIsRemoving(true);
    setTimeout(() => {
      onClose(id);
    }, 350); // Match exit animation duration in app.css
  };

  return (
    <div className={`toast-item-wrap ${isVisible && !isRemoving ? 'is-in' : ''}`}>
      <div className="toast-item-inner">
        <div
          className={`toast-item toast-${type}`}
          style={{ '--toast-accent': accent } as React.CSSProperties}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <div className="toast-item-icon">
            {type === 'working' ? (
              <BiaSpinner size="sm" />
            ) : (
              <BiaIcon icon={getIcon() as IconProp} />
            )}
          </div>
          <span className="toast-item-text">{text}</span>
          {type !== 'working' && (
            <button
              type="button"
              className="toast-item-close"
              onClick={handleClose}
              aria-label="Close"
            >
              <BiaIcon icon="times" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Toast: React.FC = () => {
  const notifications = useSelector(selectNotifications);
  const { hideToast } = useToast();

  if (!notifications || notifications.length === 0) {
    return null;
  }

  return (
    <div
      className="toast-container"
      aria-live="polite"
      aria-atomic="true"
    >
      {notifications.map((notification) => (
        <ToastItem
          key={notification.id}
          id={notification.id}
          text={notification.text}
          type={notification.type}
          onClose={hideToast}
        />
      ))}
    </div>
  );
};

export default Toast;